import React, { useRef } from "react";
import { useIonViewWillLeave } from "@ionic/react";

import {
  IComment,
  IRealization,
  ITimelinePagination,
  ReactableType,
} from "../../state";
import RealizationItem from "../../molecules/RealizationItem";
import TimelineEntryDetailModal from "../../organisms/TimelineEntryDetailModal";
import useIntersection from "../../hooks/useIntersection";
import type { Stamp } from "../../libs/stamp";

import styles from "./TimelineTheme.module.scss";

export interface TimelineThemeProps {
  isDisplaying: boolean;
  calling: boolean;
  current_id: number;
  realizations: IRealization[];
  comments: IComment[];
  searchText: string;
  student_id: number;
  geartheme_pagination: ITimelinePagination;
  loadRealizations: () => void;
  createReport: (params: {
    realization_id?: number;
    comment_id?: number;
  }) => void;
  createComment: (realization_id: number, content: string) => void;
  updateComment: (comment_id: number, content: string) => void;
  deleteComment: (comment_id: number) => void;
  fetchComments: (realization_id: number) => void;
  clearOldComments: () => void;
  createStampReaction?: (
    realization_id: number,
    type: ReactableType,
    stamp: Stamp,
  ) => void;
  addFavorite?: (id: number) => void;
  removeFavorite?: (id: number) => void;
}

const TimelineTheme: React.FC<TimelineThemeProps> = ({
  isDisplaying,
  calling,
  current_id,
  realizations,
  comments,
  searchText,
  student_id,
  geartheme_pagination,
  loadRealizations,
  createReport,
  createComment,
  updateComment,
  deleteComment,
  fetchComments,
  clearOldComments,
  createStampReaction,
  addFavorite,
  removeFavorite,
}) => {
  const [state, setState] = React.useState({
    selectedRealizationID: 0,
  });

  const loadMoreRef = useRef<HTMLDivElement>(null);

  const selectedRealization = React.useMemo(
    () => realizations.find(r => r.id === state.selectedRealizationID),
    [realizations, state.selectedRealizationID],
  );

  useIonViewWillLeave(() => {
    setState(prevState => ({ ...prevState, selectedRealizationID: 0 }));
  });

  useIntersection({
    target: loadMoreRef,
    enabled:
      isDisplaying &&
      !calling &&
      !!realizations.length &&
      geartheme_pagination.has_next,
    onIntersect: () => loadRealizations(),
  });

  return (
    <div className={styles.wrapper}>
      {selectedRealization && (
        <TimelineEntryDetailModal
          current_id={current_id}
          realization={selectedRealization}
          isOtherStudent={student_id !== selectedRealization.student_id}
          comments={comments}
          showDetail={!!selectedRealization}
          onClose={() => setState(s => ({ ...s, selectedRealizationID: 0 }))}
          deleteComment={deleteComment}
          createReport={createReport}
          createComment={createComment}
          updateComment={updateComment}
          clearOldComments={clearOldComments}
          createStampReaction={createStampReaction}
          addFavorite={addFavorite}
          removeFavorite={removeFavorite}
          hideUserInfo
        />
      )}
      {realizations.map(realization => (
        <RealizationItem
          key={realization.id}
          calling={calling}
          realization={realization}
          searchText={searchText}
          isOtherStudent={student_id !== realization.student_id}
          addFavorite={addFavorite}
          removeFavorite={removeFavorite}
          onClick={() => {
            setState(s => ({ ...s, selectedRealizationID: realization.id }));
            fetchComments(realization.id);
          }}
        />
      ))}
      <div className={styles.loadMore} ref={loadMoreRef}>
        {calling ? "読み込み中" : ""}
      </div>
    </div>
  );
};

export default TimelineTheme;
